import React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as settingActions from '../../redux/actions/SettingsActions';
import * as authenticationActions from '../../redux/actions/AuthenticationActions';
import * as userProfileActions from '../../redux/actions/UserProfileActions';
import { updateGeosuggestLanguageSettings } from '../../redux/actions/UtilsActions';
import { toastr } from 'react-redux-toastr';

import IdleTimer from './IdleTimer';
import LoadingSpinner from '../commonComponents/util/LoadingSpinner';
import Header from './Header';
import ProtectedPage from './ProtectedPage';
import ApplicationsPage from '../applicationsComponents/applications/ApplicationsPage';
import CreateApplicationPage from '../applicationsComponents/newApplication/CreateApplicationPage';
import PrintingPage from '../printingComponents/PrintingPage';
import RecipientAccountsPage from '../recipientAccountsComponents/recipientAccounts/RecipientAccountsPage';
import RecipientAccountDetailsPage from '../recipientAccountsComponents/recipientAccountDetails/RecipientAccountDetailsPage';
import ReferencesPage from '../recipientAccountsComponents/references/ReferencesPage';
import DuplicateRecipientsPage from '../recipientAccountsComponents/manageDuplicates/DuplicateRecipientsPage';
import CampaignsPage from '../campaignComponents/campaigns/CampaignsPage';
import CampaignDetailsPage from '../campaignComponents/campaignDetails/CampaignDetailsPage';
import CampaignListsPage from '../campaignComponents/campaignLists/CampaignListsPage';
import CampaignListDetailsPage from '../campaignComponents/campaignListDetails/CampaignListDetailsPage';
import CampaignListApplicationsPage from '../campaignComponents/campaignListDetails/CampaignListApplicationsPage';
import FundsPage from '../accountingComponents/funds/FundsPage';
import FundDetailsPage from '../accountingComponents/fundDetails/FundDetailsPage';
import UserSettingsPage from '../settingComponents/userSettings/UserSettingsPage';
import SystemSettings from '../settingComponents/systemSettings/SystemSettingsPage';
import CheckPrintingPage from '../settingComponents/checkPrinting/CheckPrintingPage';
import { PermissionTypes } from '../../lib/systemVariables';
import { Helmet } from 'react-helmet';

class Layout extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            ready: false
        };
    }

    componentWillMount() {
        this.startupRequests();
    }

    async logout(redirect, navOnly) {
        if (!navOnly) {
            await this.props.actions.logout();
        }
        if (navOnly || this.props.logout.success) {
            this.props.history.push("/login", redirect ? { from: this.props.location.pathname + this.props.location.search } : {});
        }
    }

    async startupRequests() {
        await Promise.all([
            this.props.actions.getSystemSettings(),
            this.props.actions.getTags()
        ]);

        if (this.props.settings.systemSettings.success) {
            this.setState({ ready: true });
        } else {
            toastr.error("Something went wrong", "We were unable to properly retrieve system settings.  Please try refreshing the page.");
        }
    }

    handleScriptInject({ scriptTags }) {
        if (scriptTags) {
            if (window.google) window.google = undefined;
            this.props.actions.updateGeosuggestLanguageSettings({ loading: true });
            const scriptTag = scriptTags[0];
            scriptTag.onload = this.handleOnLoad.bind(this);
        }
    }

    handleOnLoad(script) {
        this.props.actions.updateGeosuggestLanguageSettings({ loading: false });
    }

    render() {
        return (
            <IdleTimer logout={this.logout.bind(this)} refreshUrl="/account/refreshToken">
                <Helmet defer={false} onChangeClientState={(newState, addedTags) => this.handleScriptInject(addedTags)}>
                    <script async={false} type="text/javascript" src={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places${this.props.utils.geosuggestLanguageSettings.language ? `&language=${this.props.utils.geosuggestLanguageSettings.language}` : ''}`} />
                </Helmet>

                {this.props.location.pathname !== '/print' &&
                    <Header userProfile={this.props.userProfile} userPermissions={this.props.userPermissions}
                        loggingOut={this.props.logout.loading} logout={this.logout.bind(this)}
                        updateAiSummaryPreference={this.props.actions.updateAiSummaryPreference} />}
                {!this.state.ready ? <div className="page"><LoadingSpinner height="35" /></div> : <Switch>
                    <Route exact path="/applications" render={props => <ProtectedPage permitted={this.props.userPermissions.viewApplications}>
                        <ApplicationsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/applications/create-application/:recipientId?" render={props => <ProtectedPage permitted={this.props.userPermissions.manageApplications}>
                        <CreateApplicationPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/print" render={props => <ProtectedPage permitted={this.props.userPermissions.printPayments}>
                        <PrintingPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/recipient-accounts" render={props => <ProtectedPage permitted={this.props.userPermissions.viewRecipients}>
                        <RecipientAccountsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/recipient-accounts/references" render={props => <ProtectedPage permitted={this.props.userPermissions.viewRecipients}>
                        <ReferencesPage {...props} />
                    </ProtectedPage>} />
                    {/* <Route exact path="/recipient-accounts/duplicates" render={props => <ProtectedPage permitted={this.props.userPermissions.manageRecipients}>
                        <DuplicateRecipientsPage {...props} />
                    </ProtectedPage>} /> */}
                    <Route exact path="/recipient-accounts/:id" render={props => <ProtectedPage permitted={this.props.userPermissions.viewApplications || this.props.userPermissions.viewRecipients}>
                        <RecipientAccountDetailsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/campaigns" render={props => <ProtectedPage permitted={this.props.userPermissions.viewCampaigns}>
                        <CampaignsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/campaigns/lists" render={props => <ProtectedPage permitted={this.props.userPermissions.viewCampaignLists}>
                        <CampaignListsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/campaigns/:id" render={props => <ProtectedPage permitted={this.props.userPermissions.viewCampaigns}>
                        <CampaignDetailsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/campaigns/:campaignId/lists/:listId/applications" render={props => <ProtectedPage permitted={this.props.userPermissions.viewCampaignLists}>
                        <CampaignListApplicationsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/campaigns/lists/:id" render={props => <ProtectedPage permitted={this.props.userPermissions.viewCampaignLists}>
                        <CampaignListDetailsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/accounting" render={props => <ProtectedPage permitted={this.props.userPermissions.viewAccounting}>
                        <FundsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/accounting/:id" render={props => <ProtectedPage permitted={this.props.userPermissions.viewAccounting}>
                        <FundDetailsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/settings/user" render={props => <ProtectedPage permitted={this.props.userPermissions.admin}>
                        <UserSettingsPage {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/settings/system" render={props => <ProtectedPage permitted={this.props.userPermissions.admin}>
                        <SystemSettings {...props} />
                    </ProtectedPage>} />
                    <Route exact path="/settings/printing" render={props => <ProtectedPage permitted={this.props.userPermissions.admin}>
                        <CheckPrintingPage {...props} />
                    </ProtectedPage>} />
                    <Redirect path='/settings' to='/settings/user' />
                    <Redirect exact path="/" to="/applications" />
                    <Redirect path='*' to='/404' />
                </Switch>}
            </IdleTimer>
        );
    }
}

function mapStateToProps(state, ownProps) {
    return {
        settings: state.settings,
        logout: state.authentication.logout,
        userProfile: state.userProfile.profile.data,
        userPermissions: {
            viewApplications: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsView) >= 0,
            manageApplications: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsManage) >= 0,
            printPayments: state.userProfile.permissions.data.indexOf(PermissionTypes.PaymentsPrint) >= 0,
            viewRecipients: state.userProfile.permissions.data.indexOf(PermissionTypes.RecipientAccntsView) >= 0,
            manageRecipients: state.userProfile.permissions.data.indexOf(PermissionTypes.RecipientAccntsManage) >= 0,
            viewCampaigns: state.userProfile.permissions.data.indexOf(PermissionTypes.CampaignAndListsView) >= 0,
            viewCampaignLists: (
                state.userProfile.permissions.data.indexOf(PermissionTypes.CampaignAndListsView) >= 0
                && state.userProfile.permissions.data.indexOf(PermissionTypes.IndividualsManage) >= 0
            ),
            viewAccounting: state.userProfile.permissions.data.indexOf(PermissionTypes.AccountingView) >= 0,
            admin: state.userProfile.permissions.data.indexOf(PermissionTypes.UsersAndSettingsManage) >= 0
        },
        utils: state.utils
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({ ...settingActions, ...authenticationActions, ...userProfileActions, updateGeosuggestLanguageSettings }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Layout);



// WEBPACK FOOTER //
// ./src/components/layoutComponents/Layout.jsx